import React, { useEffect, useState } from 'react';
import { Text } from '../../components/base/Text';
import { TextInput } from '../../components/base/TextInput';
import { useGetFlowPredefinedSteps } from '../../networking/useFlow';
import { isNonEmpty } from '../../utils/arrayUtils';
import { Button } from '../../components/base/Button';
import { Steps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { FlowStep } from './FlowStep';
import { MarkdownText } from '../../components/base/MarkdownText';
import './FlowForm.css';
/**
 * Form to create or edit a flow.
 * It displays the flow name, description and a list of steps.
 * The steps are displayed as a list of FlowStep components, and each step can
 * be an event or an Agent to execute a certain action
 *
 * @param entity - The flow entity to be created or edited
 * @param isEditing - If the form is in editing mode
 * @param setEntity - Function to set the flow entity
 * @param onSave - Function to be called when saving the flow
 * @param style - Style to be applied to the form
 * @returns
 */
export const FlowForm = ({ entity, isEditing, setEntity, onSave, style }) => {
    const { isLoading: isLoadingSteps, data, call } = useGetFlowPredefinedSteps();
    const [flowSteps, setFlowSteps] = useState([]);
    useEffect(() => {
        if (data && isNonEmpty(data)) {
            setFlowSteps(data);
        }
    }, [data]);
    useEffect(() => {
        call();
    }, []);
    const getSteps = ({ stepType }) => {
        return flowSteps.filter((step) => step.type === stepType);
    };
    const getStep = (stepSlug) => {
        for (let step of flowSteps) {
            if (step.slug === stepSlug)
                return step;
        }
    };
    const simpleFields = {
        name: 'Name',
        description: 'Description',
    };
    return (React.createElement(React.Fragment, null,
        Object.keys(simpleFields).map((key, index) => {
            const label = simpleFields[key];
            const value = entity[key];
            return (React.createElement("div", { key: index, className: "flex-row", style: { gap: 12 } },
                React.createElement(Text, { style: { alignSelf: 'center', width: '33%', fontWeight: 700 }, value: `${label}:` }),
                !isEditing ? (React.createElement(Text, { value: value })) : (React.createElement(TextInput, { value: value, setText: (text) => setEntity((entity) => ({
                        ...entity,
                        [key]: text,
                    })), maxLines: 3, style: {} }))));
        }),
        React.createElement("div", { className: "flex-column", style: { gap: 12 } },
            React.createElement(MarkdownText, { style: { color: 'grey' }, markdownText: `Every flow is constituted by an **event** and a list of actions. \\
          When the event is triggered, all the actions will be executed automatically in order. \\
          If no event is defined, the flow can only be triggered manually pressing the 'Test' button.` }),
            React.createElement(Text, { style: { fontWeight: 700 }, value: `Steps:` }),
            isLoadingSteps ? (React.createElement("div", null, "Loading steps...")) : !isNonEmpty(entity.steps) && isNonEmpty(flowSteps) && isEditing ? (React.createElement(Button, { text: "Create new Event and Action", style: { marginTop: 16 }, onClick: () => {
                    setEntity((flow) => ({
                        ...flow,
                        step_slugs: [getSteps({ stepType: 'event' })[0].slug],
                        steps: [getStep(getSteps({ stepType: 'event' })[0].slug)],
                    }));
                } })) : !isNonEmpty(entity.steps) ? (React.createElement("div", null, "No steps found")) : (React.createElement(React.Fragment, null,
                React.createElement(Steps, { size: "small", direction: "vertical", style: { display: 'flex', width: '100%', padding: 0, margin: 0 }, items: entity.steps.map((step, index) => {
                        if (!step) {
                            if (isNonEmpty(flowSteps)) {
                                setEntity((flow) => ({
                                    ...flow,
                                    step_slugs: entity.step_slugs.filter((_, i) => i !== index),
                                    steps: entity.steps.filter((_, i) => i !== index),
                                }));
                            }
                            return null;
                        }
                        return {
                            icon: (React.createElement("div", { style: { ...circleStyle, backgroundColor: step.type === 'event' ? 'green' : 'blue' } }, index + 1)),
                            title: (React.createElement(FlowStep, { key: index, step: step, isEditing: isEditing, predefinedSteps: flowSteps, setStep: (newStep) => {
                                    setEntity((flow) => ({
                                        ...flow,
                                        step_slugs: entity.step_slugs.map((oldSlug, i) => (i === index ? newStep.slug : oldSlug)),
                                        steps: entity.steps.map((oldStep, i) => (i === index ? newStep : oldStep)),
                                    }));
                                }, deleteStep: () => {
                                    setEntity((flow) => {
                                        return {
                                            ...flow,
                                            step_slugs: entity.step_slugs.filter((_, i) => i !== index),
                                            steps: entity.steps.filter((_, i) => i !== index),
                                        };
                                    });
                                } })),
                        };
                    }) }),
                isEditing ? (React.createElement(React.Fragment, null,
                    !isNonEmpty(entity.steps.filter((step) => step.type === 'event')) && (React.createElement("div", { className: "flex-row clickable", style: { textAlign: 'left', gap: 8, fontWeight: 700 }, onClick: () => {
                            setEntity((flow) => ({
                                ...flow,
                                step_slugs: [getSteps({ stepType: 'event' })[0].slug, ...flow.step_slugs],
                                steps: [structuredClone(getStep(getSteps({ stepType: 'event' })[0].slug)), ...flow.steps],
                            }));
                        }, onKeyDown: () => { }, role: "button", tabIndex: 0 },
                        React.createElement(PlusOutlined, null),
                        React.createElement(Text, { value: "Add Event" }))),
                    React.createElement("div", { className: "flex-row clickable", style: { textAlign: 'left', gap: 8, fontWeight: 700 }, onClick: () => {
                            setEntity((flow) => ({
                                ...flow,
                                step_slugs: [...flow.step_slugs, getSteps({ stepType: 'agent' })[0].slug],
                                steps: [...flow.steps, structuredClone(getStep(getSteps({ stepType: 'agent' })[0].slug))],
                            }));
                        }, onKeyDown: () => { }, role: "button", tabIndex: 0 },
                        React.createElement(PlusOutlined, null),
                        React.createElement(Text, { value: "Call Agent" })),
                    React.createElement(Button, { text: "Save flow", style: { marginTop: 16 }, onClick: onSave }))) : null)))));
};
const circleStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '16px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#1890ff',
    color: 'white',
    fontWeight: 'bold',
};
